import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { tg } from "../telegram";
import { ru } from "./ru";
import { en } from "./en";
import { ps } from "./ps";
import { fa } from "./fa";

export type Lang = "ru" | "en" | "ps" | "fa";
export const SUPPORTED: Lang[] = ["ru", "en", "ps", "fa"];
const DEFAULT: Lang = "ru";

const CATALOGS: Record<Lang, Record<string, string>> = { ru, en, ps, fa };
const RTL_LANGS = new Set<Lang>(["ps", "fa"]);

export function isRtl(lang: Lang): boolean {
  return RTL_LANGS.has(lang);
}

export function normalizeLang(code: string | undefined | null): Lang {
  if (!code) return DEFAULT;
  const base = code.toLowerCase().replace("_", "-").split("-")[0];
  // Telegram/browser may report Dari as "prs"/"fa", Pashto as "ps"/"pus".
  if (base === "prs" || base === "fa") return "fa";
  if (base === "ps" || base === "pus") return "ps";
  return (SUPPORTED.includes(base as Lang) ? base : DEFAULT) as Lang;
}

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);
const STORAGE_KEY = "docs.lang";

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored as Lang)) return stored as Lang;
    } catch {}
    const tgLang = tg()?.initDataUnsafe?.user?.language_code;
    return tgLang ? normalizeLang(tgLang) : DEFAULT;
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = isRtl(lang) ? "rtl" : "ltr";
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch {}
  }, []);

  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    const catalog = CATALOGS[lang] || CATALOGS.ru;
    let template = catalog[key];
    if (template === undefined) template = CATALOGS.en[key];
    if (template === undefined) return key;
    if (vars) {
      for (const k of Object.keys(vars)) {
        template = template.replaceAll(`{${k}}`, String(vars[k]));
      }
    }
    return template;
  }, [lang]);

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used inside <I18nProvider>");
  return ctx;
}

export const LANG_LABELS: Record<Lang, { label: string; emoji: string }> = {
  ru: { label: "Русский", emoji: "🇷🇺" },
  en: { label: "English", emoji: "🇬🇧" },
  ps: { label: "پښتو", emoji: "🇦🇫" },
  fa: { label: "فارسی / دری", emoji: "🇮🇷" },
};
